import { Instance, SnapshotIn, types } from 'mobx-state-tree'
import { parseBackendUtcDateString } from '../../utils/datetimeUtils'
import { DecimalType } from '../common/ModelTypes'

import { PagingMetadataModel } from '../common/PagingMetadataModel'
import { WithLatLngModel } from '../common/WithLatLng'

export enum PackageStatus {
  UNDELIVERED = 'undelivered',
  DELIVERED = 'delivered',
}

export enum PointType {
  WAYPOINT = 'waypoint',
  CENTER = 'center',
  DELIVERY = 'delivery',
  SEMAPHORE = 'semaphore',
}

const VehicleLocationModel = WithLatLngModel.named('VehicleLocationSchema')
.props({
  flight_altitude_m: DecimalType,
})

export const MissionPointModel = WithLatLngModel.named('MissionPointModel')
.props({
  id: types.identifierNumber,
  name: types.string,

  uuid: types.string,
  mission_id: types.string,
  ground_elevation_m: DecimalType,
  flight_altitude_m: DecimalType,

  point_type: types.enumeration('PointType', Object.values(PointType)),
  is_test_point: types.boolean,
})


export enum MissionLogType {
  FC = 'fc',
  MC = 'mc',
  FTS = 'fts',
}

const ExtendedFileLogModel = types.model('ExtendedFileLogModel',  {
  id: types.maybe(types.maybeNull(types.string)),
  log_type: types.maybe(types.maybeNull(types.string)),
  name: types.maybe(types.maybeNull(types.string)),
  s3_path: types.maybe(types.maybeNull(types.string)),
  log_file_url: types.maybe(types.maybeNull(types.string)),
})
type ExtendedFileLog = Instance<typeof ExtendedFileLogModel>

export const MissionHistoryExtraDataModel = types.model('MissionHistoryExtraDataModel').props({
  id: types.identifier,
  reported_route: types.maybe(types.maybeNull(types.array(VehicleLocationModel))),
  telemetry_file_url: types.maybe(types.maybeNull(types.string)),
  log_files: types.maybe(types.array(ExtendedFileLogModel))
})
.views((self) => ({
  logFile(logType: MissionLogType): ExtendedFileLog | null {
    return self.log_files?.find(logFile => logFile.log_type === logType) ?? null
  }
}))

export const MissionHistoryModel = types
  .model('MissionHistoryModel')
  .props({
    id: types.identifier,

    // Note: the backend schema contains many more fields.
    // We don't use them anywhere (at least for now), so we won't add them needlessly to the model.
    takeoff_timestamp: types.maybe(types.maybeNull(types.string)),
    vehicle_name: types.maybe(types.maybeNull(types.string)),
    rpic_user_name: types.maybe(types.maybeNull(types.string)),
    order_name: types.maybe(types.maybeNull(types.string)),

    mission_note: types.maybe(types.maybeNull(types.string)),
    note_updated_by: types.maybe(types.maybeNull(types.string)),
    note_updated_by_user_name: types.maybe(types.maybeNull(types.string)),
    note_updated_by_id: types.maybe(types.maybeNull(types.number)),
    note_update_timestamp: types.maybe(types.maybeNull(types.string)),

    center_point: types.maybe(types.maybeNull(MissionPointModel)),
    delivery_point: types.maybe(types.maybeNull(MissionPointModel)),

    planned_route: types.maybe(types.maybeNull(types.array(MissionPointModel))),
    package_status: types.enumeration('PackageStatus', Object.values(PackageStatus)),
  })
  .views((self) => ({
    get shortId(): string {
      return self.id.substring(0, 6)
    },
    get takeoffDate(): Date | undefined | null {
      if (self.takeoff_timestamp === undefined || self.takeoff_timestamp === null) return self.takeoff_timestamp
      return parseBackendUtcDateString(self.takeoff_timestamp)
    },
    get noteUpdateTimestamp(): Date  | undefined | null {
      if (self.note_update_timestamp === undefined || self.note_update_timestamp === null) return self.note_update_timestamp
      return parseBackendUtcDateString(self.note_update_timestamp)
    },
    getMapBounds(): google.maps.LatLngBounds | undefined {
      if (self.planned_route === undefined || self.planned_route === null || self.planned_route.length < 2) {
        return undefined
      }

      const mapBounds = new google.maps.LatLngBounds()
      self.planned_route.map((point) => mapBounds.extend(point.toGmapLatLng))
      return mapBounds
    },
  }))

export const MissionHistoryReportModel = types.model('MissionHistoryReportModel').props({
  missions: types.array(MissionHistoryModel),
  metadata: PagingMetadataModel,
})

export type MissionHistoryReportSnapshot = SnapshotIn<typeof MissionHistoryReportModel>
export type MissionHistoryReport = Instance<typeof MissionHistoryReportModel>
export type MissionHistory = Instance<typeof MissionHistoryModel>
export type MissionHistoryExtraData = Instance<typeof MissionHistoryExtraDataModel>